import { Dispatch, SetStateAction } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { useReportId } from "@ryde/redux";
import {
  Log,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
  ReportPeriodGroupingEnum,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsVisibilityAF,
} from "@ryde/shared";

import { shouldSyncReportGroupingForChartDimension } from "./report-chart.utils.data";

export const useReportChartDimensionChange = ({
  groupingParamKeys,
  flexGroupingTemplateKeys,
  isFlexPeriodApplied,
  setDimensionColumnKey,
}: {
  groupingParamKeys: string[];
  flexGroupingTemplateKeys: string[];
  isFlexPeriodApplied: boolean;
  setDimensionColumnKey: Dispatch<SetStateAction<string>>;
}) => {
  const { reportId } = useReportId();
  const [groupingParams, setGroupingParams] = useRecoilState(
    reportFlexGroupingParamsAF(reportId),
  );
  const [periodGrouping, setPeriodGrouping] = useRecoilState(
    reportGroupingPeriodAF(reportId),
  );
  const setColumnVisibilityState = useSetRecoilState(
    reportsTableColumnsVisibilityAF(reportId),
  );
  const setColumnOrderState = useSetRecoilState(
    reportsTableColumnsOrderStateAF(reportId),
  );

  const resetReportLayoutOnGroupingChange = useEvent(() => {
    Log.debug("Reset report layout on chart dimension grouping sync");
    setColumnVisibilityState({});
    setColumnOrderState([]);
  });

  const onDimensionChange = useEvent((dimensionKey: string) => {
    setDimensionColumnKey(dimensionKey);

    const groupingSync = shouldSyncReportGroupingForChartDimension({
      dimensionKey,
      groupingParamKeys: groupingParams ?? groupingParamKeys,
      flexGroupingTemplateKeys,
      isFlexPeriodApplied,
    });

    if (!groupingSync) {
      return;
    }

    resetReportLayoutOnGroupingChange();

    if (groupingSync.enablePeriodGrouping) {
      if (periodGrouping === ReportPeriodGroupingEnum.EntirePeriod) {
        setPeriodGrouping(ReportPeriodGroupingEnum.Monthly);
      }

      return;
    }

    if (groupingSync.appendFlexGroupingKey) {
      const currentParams = groupingParams ?? groupingParamKeys;

      if (currentParams.includes(groupingSync.appendFlexGroupingKey)) {
        return;
      }

      setGroupingParams([...currentParams, groupingSync.appendFlexGroupingKey]);
    }
  });

  return { onDimensionChange };
};
